import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Stars from "@/components/Stars";
import { Star, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

function RatingInput({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="inline-flex items-center gap-1" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`rating-star-${n}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          className="p-0.5 rounded transition-transform hover:scale-110"
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
        >
          <Star className={`w-7 h-7 ${(hover || value) >= n ? "text-brand fill-current" : "text-zinc-200"}`} />
        </button>
      ))}
    </div>
  );
}

function WriteReviewForm({ productId, onSubmitted }) {
  const { user } = useAuth();
  const { t } = useLang();
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user) {
    return (
      <div data-testid="review-signin-prompt" className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 text-center">
        <div className="text-sm text-zinc-600">{t("reviews.signInPrompt")}</div>
        <Link to="/login" className="mt-3 inline-block text-brand font-semibold hover:underline text-sm">{t("reviews.signIn")}</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) { toast.error(t("reviews.pickRating")); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, title, comment });
      toast.success(t("reviews.posted"));
      setRating(0); setTitle(""); setComment("");
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} data-testid="write-review-form" className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{t("reviews.writeReview")}</div>
      <div>
        <Label className="text-sm font-semibold text-zinc-800">{t("reviews.yourRating")}</Label>
        <div className="mt-1.5"><RatingInput value={rating} onChange={setRating} /></div>
      </div>
      <div>
        <Label className="text-sm font-semibold text-zinc-800">{t("reviews.title")}</Label>
        <Input data-testid="review-title" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={120} placeholder={t("reviews.titlePlaceholder")} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
      </div>
      <div>
        <Label className="text-sm font-semibold text-zinc-800">{t("reviews.yourReview")}</Label>
        <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} required rows={4} placeholder={t("reviews.reviewPlaceholder")} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200" />
      </div>
      <Button data-testid="submit-review-btn" type="submit" disabled={submitting} className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
        {submitting ? t("reviews.posting") : t("reviews.postReview")}
      </Button>
    </form>
  );
}

export default function ProductReviews({ productId, onRatingChange }) {
  const { user } = useAuth();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/products/${productId}/reviews`);
      setData(data);
      onRatingChange?.(data.avg, data.count);
    } finally { setLoading(false); }
  }, [productId, onRatingChange]);

  useEffect(() => { load(); }, [load]);

  const removeReview = async (rid) => {
    if (!window.confirm(t("reviews.deleteConfirm"))) return;
    try {
      await api.delete(`/products/${productId}/reviews/${rid}`);
      toast.success(t("reviews.deleted"));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="text-sm text-zinc-500 py-6">{t("reviews.loading")}</div>;

  const total = data.count || 0;
  const breakdown = data.breakdown || {};
  const alreadyReviewed = user && data.reviews.some((r) => r.user_id === user.user_id);

  return (
    <section data-testid="product-reviews-section" className="pt-14 mt-14 border-t border-zinc-100">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("reviews.customerReviews")}</div>
          <div className="mt-2 flex items-end gap-3">
            <div className="font-display font-extrabold text-5xl tracking-tighter text-zinc-900" data-testid="review-avg">
              {total > 0 ? data.avg.toFixed(1) : "—"}
            </div>
            <div className="pb-2">
              <Stars value={data.avg} size={18} />
              <div className="text-xs text-zinc-500 mt-1" data-testid="review-count">{total} {t("product.reviews")}</div>
            </div>
          </div>

          <div className="mt-6 space-y-2">
            {[5, 4, 3, 2, 1].map((n) => {
              const c = breakdown[String(n)] || 0;
              const pct = total > 0 ? (c / total) * 100 : 0;
              return (
                <div key={n} className="flex items-center gap-3 text-xs text-zinc-500">
                  <div className="w-6 flex items-center gap-0.5"><span className="text-zinc-800 font-semibold">{n}</span><Star className="w-3 h-3 text-brand fill-current" /></div>
                  <div className="flex-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                    <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-6 text-right">{c}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="md:col-span-8">
          {!alreadyReviewed && <WriteReviewForm productId={productId} onSubmitted={load} />}
          {alreadyReviewed && <div data-testid="already-reviewed" className="bg-emerald-50 border border-emerald-100 text-emerald-800 rounded-2xl px-5 py-3 text-sm">{t("reviews.alreadyReviewed")}</div>}

          <div className="mt-8 space-y-6" data-testid="reviews-list">
            {data.reviews.length === 0 && (
              <div className="text-sm text-zinc-500 py-6">{t("reviews.noReviews")}</div>
            )}
            {data.reviews.map((r) => (
              <article key={r.id} data-testid={`review-${r.id}`} className="border-b border-zinc-100 pb-6 last:border-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Stars value={r.rating} size={14} />
                      {r.verified_purchase && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                          <ShieldCheck className="w-3 h-3" /> {t("reviews.verified")}
                        </span>
                      )}
                    </div>
                    <h4 className="mt-2 font-display font-bold text-lg tracking-tight text-zinc-900">{r.title}</h4>
                    <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{r.comment}</p>
                    <div className="mt-2 text-xs text-zinc-500">
                      <span className="font-semibold text-zinc-700">{r.user_name}</span> · {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  {user && (r.user_id === user.user_id || user.role === "admin") && (
                    <button data-testid={`delete-review-${r.id}`} onClick={() => removeReview(r.id)} className="text-zinc-300 hover:text-brand transition-colors p-1" aria-label="Delete review">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
